/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDialogContentStyleProps, IDialogContentStyles } from '@fluentui/react/lib/Dialog';
import { IStackProps, IStackStyles, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { AzureResourceGroupStore } from 'src/core/stores/AzureResourceGroupStore';
import { AzureSubscriptionStore } from 'src/core/stores/AzureSubscriptionStore';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { Region } from 'src/core/types/regionTypes';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { useAzureMigration } from 'src/verticals/azureMigrationModal/hooks/useAzureMigration';
import { useMultiRegionMigrationStateMachine } from 'src/verticals/azureMigrationModal/hooks/useMultiRegionMigrationStateMachine';
import { AuthoringResourceContainerScreen } from 'src/verticals/azureMigrationModal/newComponents/AuthoringResourceContainerScreen';
import { EmailCollaboratorsScreen } from 'src/verticals/azureMigrationModal/newComponents/EmailCollaboratorsScreen';
import { ExportAppsScreen } from 'src/verticals/azureMigrationModal/newComponents/ExportAppsScreen';
import { FinishScreen } from 'src/verticals/azureMigrationModal/newComponents/FinishScreen';
import { IntroductionScreen } from 'src/verticals/azureMigrationModal/newComponents/IntroductionScreen';
import { MigrationModalStepsNavigator } from 'src/verticals/azureMigrationModal/newComponents/MigrationModalStepsNavigator';
import { RegionMigrationScreen } from 'src/verticals/azureMigrationModal/newComponents/RegionMigrationScreen';
import { UserMigrationInfoStore } from 'src/verticals/azureMigrationModal/stores/UserMigrationInfoStore';
import { MultiRegionMigrationSteps } from 'src/verticals/azureMigrationModal/types/azureMigrationModalTypes';

export type AzureMigrationModalCsAccountDto = {
    /**
     * The name of the azure cs account to be used in migrating the user.
     */
    name: string;

    /**
     * The azure subscription to be used in migrating the user.
     */
    subscriptionId: AzureSubscriptionStore['id'];

    /**
     * The azure resource group to be used in migrating the user.
     */
    resourceGroupName: AzureResourceGroupStore['name'];
};

const messages = defineMessages({
    migrationToastStarted: {
        id: 'multiRegionMigrationModal.migrationToastStarted',
        defaultMessage: 'Migrating ...'
    },
    migrationToastCompleted: {
        id: 'multiRegionMigrationModal.migrationToastCompleted',
        defaultMessage: 'Migration complete!'
    },
    migrationToastFailed: {
        id: 'multiRegionMigrationModal.migrationToastFailed',
        defaultMessage: 'Migration failed'
    },
    title: {
        id: 'multiRegionMigrationModal.title',
        defaultMessage: 'Migrate to Azure'
    }
});

const modalTheme = getScopedTheme('PrepareContainerModal');
const dialogContentStyles = classNamesFunction<IDialogContentStyleProps, IDialogContentStyles>()({ inner: { paddingTop: '30px' } });
const contentStyles = classNamesFunction<IStackProps, IStackStyles>()({
    root: { minHeight: '550px', marginRight: '30px', color: modalTheme.defaultTextColor, minWidth: 0 }
});

/**
 * Note: Do NOT use this outside the multi region migration
 * modal as this is used only for the time at which the EU
 * migration is in place.
 * @deprecated
 */
export const getBackwardCompatibleRegion = () => {
    switch (window.location.hostname) {
        case 'www.luis.ai':
        case 'language.azure.com':
        case 'language.cognitive.azure.com':
        case 'dialogice3.azurewebsites.net':
        case 'dialogice4.azurewebsites.net':
        case 'dialogice6.azurewebsites.net':
        case 'dialogice10.azurewebsites.net':
        case 'dialogice14.azurewebsites.net':
            return 'westus';
        case 'eu.luis.ai':
            return 'westeurope';
        case 'luis.azure.cn':
            return 'chinaeast2';
        case 'luis.azure.us':
            return 'usgovvirginia';
        case 'www.luis.eaglex.ic.gov':
            return 'usnatwest';
        case 'canary.luis.ai':
            return 'centraluseuap';
        default:
            return 'westus';
    }
};

/**
 * Display the multi region migration modal.
 */
export const MultiRegionMigrationModal = withModalMounter((props: BasicModalProps<object>) => {
    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();
    const { isLargeOrGreater } = useResponsiveMode();

    const { appsToEmail, appsToEmailTracker, appsToExport, appsToExportTracker } = useAzureMigration();
    const { isInitializing, currentScreen, getScreen } = useMultiRegionMigrationStateMachine({
        appsToEmail,
        appsToExport,
        appsToEmailTracker,
        appsToExportTracker
    });
    const tracker = useProgressTracker({ onComplete: () => getScreen('nextScreen') });
    const migrationInfo = rootStore.userMigrationInfoStoreHouse.items;

    const onMigrate = (csAccountDto: AzureMigrationModalCsAccountDto, userMigrationInfo: UserMigrationInfoStore) => {
        dispatcher.dispatch('migrate', {
            tracker,
            accountName: csAccountDto.name,
            region: userMigrationInfo.region,
            endpoint: userMigrationInfo.endpoint,
            serviceKey: userMigrationInfo.serviceKey,
            subscriptionId: csAccountDto.subscriptionId,
            resourceGroupName: csAccountDto.resourceGroupName,
            toastData: {
                running: { title: localize(messages.migrationToastStarted) },
                completed: { title: localize(messages.migrationToastCompleted) },
                failed: { title: localize(messages.migrationToastFailed) }
            }
        });
    };

    const renderRegionMigrationScreens = (region: Region): JSX.Element => {
        const userMigrationInfoStore = migrationInfo.find(store => store.region === region);

        return (
            <AuthoringResourceContainerScreen
                isLoading={tracker.isRunning}
                userMigrationInfoStore={userMigrationInfoStore}
                onContinue={() => getScreen('nextScreen')}
                onPrevious={() => getScreen('previousScreen')}
                onMigrate={dto => onMigrate(dto, userMigrationInfoStore)}
            />
        );
    };

    // Displays the screen content depending on the current active screen.
    const getStepContent = () => {
        switch (currentScreen) {
            case MultiRegionMigrationSteps.start:
                return <IntroductionScreen isInitializing={isInitializing} onContinue={() => getScreen('nextScreen')} />;

            case MultiRegionMigrationSteps.emailCollaborators:
                return (
                    <EmailCollaboratorsScreen
                        appsWithCollaborators={appsToEmail}
                        onContinue={() => getScreen('nextScreen')}
                        onPrevious={() => getScreen('previousScreen')}
                    />
                );

            case MultiRegionMigrationSteps.exportApps:
                return (
                    <ExportAppsScreen
                        appsWithOwners={appsToExport}
                        userMigrationInfo={migrationInfo}
                        onContinue={() => getScreen('nextScreen')}
                        onPrevious={() => getScreen('previousScreen')}
                    />
                );

            case MultiRegionMigrationSteps.regionalMigration:
                return <RegionMigrationScreen onContinue={() => getScreen('nextScreen')} onPrevious={() => getScreen('previousScreen')} />;

            case MultiRegionMigrationSteps.chooseCsAccount:
                return renderRegionMigrationScreens(getBackwardCompatibleRegion());

            case MultiRegionMigrationSteps.chooseUsCsAccount:
                return renderRegionMigrationScreens('westus');

            case MultiRegionMigrationSteps.chooseEuCsAccount:
                return renderRegionMigrationScreens('westeurope');

            case MultiRegionMigrationSteps.chooseAusCsAccount:
                return renderRegionMigrationScreens('australiaeast');

            case MultiRegionMigrationSteps.finish:
                return <FinishScreen hidden={false} onDismiss={() => window.location.reload()} />;

            default:
                return null;
        }
    };

    return (
        <BasicModal
            {...props}
            minWidth={850}
            shouldRenderFooter={false}
            dialogContentProps={{ title: localize(messages.title), styles: dialogContentStyles }}>
            <Stack horizontal verticalFill tokens={{ childrenGap: 70 }}>
                {isLargeOrGreater && (
                    <Stack.Item grow styles={{ root: { minWidth: 180, maxWidth: 180 } }}>
                        <MigrationModalStepsNavigator
                            activeStep={currentScreen}
                            userMigrationInfo={migrationInfo}
                            hideEmailStep={appsToEmail?.length === 0}
                            hideExportStep={appsToExport?.length === 0}
                        />
                    </Stack.Item>
                )}

                <Stack.Item grow styles={contentStyles}>
                    {getStepContent()}
                </Stack.Item>
            </Stack>
        </BasicModal>
    );
});
